import React, { useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import Tabela from "../../../../componentsv2/Tabela";
import Paper from "../../../../componentsv2/Paper";
import Button from "../../../../componentsv2/Button";
import GridContainer from "../../../../componentsv2/GridContainer";
import GridItem from "../../../../componentsv2/GridItem";
import { HStack } from "../../../../componentsv2/Stack";
import formatDateBD from "../../../../utils/formatarDataBD";
import getApiErrorMessage from "../../../../utils/getApiErrorMessage";
import { api } from "../../../../services/Api";
import DataPickerAtualizado from "../../../../components/DataPickerAtualizado";
import SelectEnfermeiro from "../../Components/SelectEnfermeiro";
import CardTotal from "../../../ColetaDomiciliar/Components/Financeiro/Totalizadores/CardTotal";
import { formatarMoedaBrl } from "../../utils/descontoAgendamento";
import { validarPeriodoAgenda } from "../../utils/validarPeriodoAgenda";
import "./style.css";

const campoFiltroSx = {
  display: "flex",
  alignItems: "flex-end",
  width: "100%",
  "& .MuiGrid-container": { margin: 0, width: "100%" },
  "& .MuiGrid-item": { padding: 0, maxWidth: "100%", flexBasis: "100%" },
  "& .MuiFormControl-root": { width: "100%", marginBottom: 0 },
  "& .MuiInputBase-root": { fontSize: "0.875rem" },
  "& .MuiInputLabel-root": { fontSize: "0.875rem" },
};

const datePickerSlotProps = {
  textField: {
    size: "small",
    variant: "standard",
    fullWidth: true,
  },
};

const campoIgualSx = {
  flex: { md: "1 1 0" },
  minWidth: { md: 145 },
  ...campoFiltroSx,
};

function obterPeriodoPadrao() {
  const hoje = new Date();
  const inicio = new Date(hoje);
  inicio.setDate(hoje.getDate() - 30);

  return {
    dataInicio: formatDateBD(inicio),
    dataFim: formatDateBD(hoje),
  };
}

function calcularCards(itens) {
  const cards = {
    "Crédito - Vencimento": [0, 0],
    "Crédito - Parcelado": [0, 0],
    Boleto: [0, 0],
    Pix: [0, 0],
    "Debito - Espécie": [0, 0],
    "Transferencia Bancária": [0, 0],
  };
  let total = [0, 0];

  for (const item of itens) {
    const valor = Number(item.totalLiquido || 0);
    total = [total[0] + valor, total[1] + 1];

    const card = cards[item.formaPagamento];
    if (card) {
      card[0] += valor;
      card[1] += 1;
    }
  }

  return {
    creditoVencimento: cards["Crédito - Vencimento"],
    creditoParcelado: cards["Crédito - Parcelado"],
    boleto: cards.Boleto,
    pix: cards.Pix,
    debitoEspecie: cards["Debito - Espécie"],
    transferenciaBancaria: cards["Transferencia Bancária"],
    total,
  };
}

export default function FinanceiroAgendaCare() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataInicio, setDataInicio] = useState(() => obterPeriodoPadrao().dataInicio);
  const [dataFim, setDataFim] = useState(() => obterPeriodoPadrao().dataFim);
  const [enfermeiroId, setEnfermeiroId] = useState("");
  const [filtrosAplicados, setFiltrosAplicados] = useState(() => {
    const periodo = obterPeriodoPadrao();
    return {
      dataInicio: periodo.dataInicio,
      dataFim: periodo.dataFim,
      enfermeiroId: "",
    };
  });

  useEffect(() => {
    let cancelado = false;

    async function carregarFinanceiro() {
      const { dataInicio: inicio, dataFim: fim, enfermeiroId: filtroEnfermeiro } =
        filtrosAplicados;

      if (!inicio || !fim) {
        return;
      }

      setLoading(true);
      setRows([]);

      try {
        const params = {
          dataInicio: inicio,
          dataFim: fim,
        };

        if (filtroEnfermeiro) {
          params.enfermeiroId = filtroEnfermeiro;
        }

        const {
          data: { data },
        } = await api.get("/v2/agendaCare/agendamentos/financeiro", { params });

        if (cancelado) {
          return;
        }

        setRows(Array.isArray(data) ? data : []);
      } catch (error) {
        if (cancelado) {
          return;
        }

        setRows([]);
        enqueueSnackbar(
          getApiErrorMessage(error, "Falha para encontrar agendamentos"),
          { variant: "error" }
        );
      } finally {
        if (!cancelado) {
          setLoading(false);
        }
      }
    }

    carregarFinanceiro();

    return () => {
      cancelado = true;
    };
  }, [filtrosAplicados]);

  const handlePesquisar = () => {
    const erroPeriodo = validarPeriodoAgenda(dataInicio, dataFim);
    if (erroPeriodo) {
      enqueueSnackbar(erroPeriodo, { variant: "warning", preventDuplicate: true });
      return;
    }

    setFiltrosAplicados({
      dataInicio,
      dataFim,
      enfermeiroId,
    });
  };

  const {
    creditoVencimento,
    creditoParcelado,
    boleto,
    pix,
    debitoEspecie,
    transferenciaBancaria,
    total,
  } = calcularCards(rows);

  const columns = [
    {
      field: "data",
      headerName: "Data",
      headerAlign: "center",
      valueGetter: ({ value }) => value && new Date(value).toLocaleDateString(),
      align: "center",
      flex: 1,
    },
    {
      field: "enfermeiroNome",
      headerName: "Enfermeiro",
      headerAlign: "center",
      align: "center",
      flex: 1,
    },
    {
      field: "formaPagamento",
      headerName: "Forma de pagamento",
      headerAlign: "center",
      align: "center",
      flex: 1,
    },
    {
      field: "totalLiquido",
      headerName: "Valor",
      valueGetter: ({ value }) => formatarMoedaBrl(Number(value || 0)),
      headerAlign: "center",
      align: "center",
      flex: 1,
    },
    {
      field: "desconto",
      headerName: "Desconto",
      headerAlign: "center",
      valueGetter: ({ value }) => formatarMoedaBrl(Number(value || 0)),
      align: "center",
      flex: 1,
    },
    {
      field: "cliente",
      headerName: "Cliente",
      headerAlign: "center",
      valueGetter: ({ value }) => value?.nome || "",
      align: "center",
      flex: 1,
    },
    {
      field: "cpf",
      headerName: "CPF",
      headerAlign: "center",
      valueGetter: ({ row }) => row?.cliente?.cpf || "",
      align: "center",
      flex: 1,
    },
    {
      field: "distancia",
      headerName: "Distancia",
      headerAlign: "center",
      align: "center",
      flex: 1,
    },
    {
      field: "medicoNome",
      headerName: "Medico",
      headerAlign: "center",
      align: "center",
      flex: 1,
    },
  ];

  return (
    <HStack>
      <Paper>
        <GridContainer
          sx={{
            display: "flex",
            alignItems: "flex-end",
            flexWrap: { xs: "wrap", md: "nowrap" },
            gap: 2,
            pl: 2,
            pr: "13px",
            py: "13px",
          }}
        >
          <GridItem xs={6} sx={campoIgualSx}>
            <DataPickerAtualizado
              data={dataInicio}
              setData={setDataInicio}
              formatarNaData
              variant="standard"
              labelName="Data inicial"
              slotProps={datePickerSlotProps}
            />
          </GridItem>

          <GridItem xs={6} sx={campoIgualSx}>
            <DataPickerAtualizado
              data={dataFim}
              setData={setDataFim}
              formatarNaData
              variant="standard"
              labelName="Data final"
              slotProps={datePickerSlotProps}
            />
          </GridItem>

          <GridItem xs={6} sx={campoIgualSx}>
            <SelectEnfermeiro
              todos={true}
              onChange={e => setEnfermeiroId(e.target.value)}
              value={enfermeiroId}
            />
          </GridItem>

          <GridItem
            xs={6}
            sx={{
              flex: { md: "0 0 130px" },
              maxWidth: { md: 130 },
              display: "flex",
              alignItems: "flex-end",
            }}
          >
            <Button
              variant="contained"
              fullWidth
              onClick={handlePesquisar}
              disabled={loading}
            >
              Pesquisar
            </Button>
          </GridItem>
        </GridContainer>
      </Paper>

      <div className="container_totalizadores">
        <CardTotal
          titulo="Crédito - Vencimento"
          valor={creditoVencimento[0]}
          quantidade={creditoVencimento[1]}
        />
        <CardTotal
          titulo="Crédito - Parcelado"
          valor={creditoParcelado[0]}
          quantidade={creditoParcelado[1]}
        />
        <CardTotal titulo="Boleto" valor={boleto[0]} quantidade={boleto[1]} />
        <CardTotal titulo="Pix" valor={pix[0]} quantidade={pix[1]} />
        <CardTotal
          titulo="Débito - Espécie"
          valor={debitoEspecie[0]}
          quantidade={debitoEspecie[1]}
        />
        <CardTotal
          titulo="Transferência bancária"
          valor={transferenciaBancaria[0]}
          quantidade={transferenciaBancaria[1]}
        />
        <CardTotal titulo="Total" valor={total[0]} quantidade={total[1]} />
      </div>

      {loading ? (
        <div className="financeiro_loading">
          <CircularProgress />
        </div>
      ) : (
        <Tabela rows={rows} columns={columns} />
      )}
    </HStack>
  );
}
